/**
 * Apple-inspired design tokens.
 *
 * Two complete palettes (light / dark) plus shared shape, motion and
 * elevation scales. The MUI theme, the CSS custom properties and the
 * marketing components all derive from this single source of truth.
 */

export const DESIGN_TOKENS = {
  typography: {
    fontFamily:
      '"SF Pro Text", "SF Pro Display", -apple-system, BlinkMacSystemFont, "Inter", "Segoe UI", Roboto, sans-serif',
    displayFontFamily:
      '"SF Pro Display", "SF Pro Text", -apple-system, BlinkMacSystemFont, "Inter", "Segoe UI", Roboto, sans-serif',
    monoFontFamily:
      '"SF Mono", ui-monospace, SFMono-Regular, "JetBrains Mono", Menlo, monospace',
  },
  shape: {
    controlRadius: 10,
    surfaceRadius: 16,
    dialogRadius: 20,
    cardRadius: 22,
    publicCardRadius: 28,
    heroRadius: 28,
    pillRadius: 980,
  },
  size: {
    controlHeight: 44,
    controlHeightSmall: 44,
    buttonHeight: 38,
    buttonHeightSmall: 32,
    iconButton: 32,
    iconButtonSmall: 28,
    tableRow: 46,
    toolbarGap: 8,
    pageGap: 18,
    filterControlWidth: 180,
    filterSelectWidth: 160,
    filterAutocompleteWidth: 220,
    filterDateWidth: 190,
    tableActionSize: 30,
    tableActionIconSize: 17,
    contentMaxWidth: 1180,
  },
  motion: {
    fast: "160ms cubic-bezier(0.32, 0.72, 0, 1)",
    base: "280ms cubic-bezier(0.32, 0.72, 0, 1)",
    slow: "620ms cubic-bezier(0.22, 0.61, 0.36, 1)",
    easing: "cubic-bezier(0.32, 0.72, 0, 1)",
    springEasing: "cubic-bezier(0.16, 1, 0.3, 1)",
  },
  mode: {
    light: {
      primary: "#0071e3",
      primaryHover: "#0077ed",
      primarySoft: "rgba(0, 113, 227, 0.10)",
      primaryContrast: "#ffffff",
      link: "#0066cc",
      accent: "#5e5ce6",
      error: "#d0342c",
      background: "#ffffff",
      backgroundAlt: "#f5f5f7",
      paper: "#ffffff",
      elevated: "#f5f5f7",
      tint: "#f5f5f7",
      tintStrong: "#e8e8ed",
      input: "#ffffff",
      text: "#1d1d1f",
      textSecondary: "#6e6e73",
      textTertiary: "#86868b",
      divider: "#d2d2d7",
      dividerSoft: "rgba(0, 0, 0, 0.08)",
      glass: "rgba(255, 255, 255, 0.72)",
      glassBorder: "rgba(0, 0, 0, 0.08)",
      scrim: "rgba(255, 255, 255, 0.86)",
      shadowSm: "0 1px 2px rgba(0, 0, 0, 0.04), 0 4px 12px rgba(0, 0, 0, 0.05)",
      shadowMd:
        "0 2px 6px rgba(0, 0, 0, 0.04), 0 12px 32px rgba(0, 0, 0, 0.08)",
      shadowLg:
        "0 4px 12px rgba(0, 0, 0, 0.05), 0 28px 64px rgba(0, 0, 0, 0.12)",
      heroGradient:
        "radial-gradient(90% 120% at 12% 0%, rgba(0, 113, 227, 0.16) 0%, rgba(0, 113, 227, 0) 58%), radial-gradient(70% 110% at 95% 12%, rgba(94, 92, 230, 0.16) 0%, rgba(94, 92, 230, 0) 62%)",
      surfaceGradient:
        "radial-gradient(90% 120% at 12% 0%, rgba(0, 113, 227, 0.04) 0%, rgba(0, 113, 227, 0) 58%), radial-gradient(70% 110% at 95% 12%, rgba(94, 92, 230, 0.04) 0%, rgba(94, 92, 230, 0) 62%)",
      tableHead: "#f5f5f7",
      tableStripe: "#fbfbfd",
      tableHover: "rgba(0, 113, 227, 0.06)",
      tableMobileBackground: "#f5f5f7",
      tableScrollbar: "#c7c7cc",
    },
    dark: {
      primary: "#0a84ff",
      primaryHover: "#409cff",
      primarySoft: "rgba(10, 132, 255, 0.16)",
      primaryContrast: "#ffffff",
      link: "#2997ff",
      accent: "#7d7aff",
      error: "#ff453a",
      background: "#000000",
      backgroundAlt: "#0a0a0c",
      paper: "#141416",
      elevated: "#1c1c1e",
      tint: "#1c1c1e",
      tintStrong: "#2c2c2e",
      input: "#131315",
      text: "#f5f5f7",
      textSecondary: "#a1a1a6",
      textTertiary: "#86868b",
      divider: "#2c2c2e",
      dividerSoft: "rgba(255, 255, 255, 0.10)",
      glass: "rgba(22, 22, 24, 0.72)",
      glassBorder: "rgba(255, 255, 255, 0.12)",
      scrim: "rgba(0, 0, 0, 0.82)",
      shadowSm: "0 1px 2px rgba(0, 0, 0, 0.4), 0 4px 14px rgba(0, 0, 0, 0.36)",
      shadowMd:
        "0 2px 8px rgba(0, 0, 0, 0.42), 0 16px 40px rgba(0, 0, 0, 0.48)",
      shadowLg:
        "0 6px 16px rgba(0, 0, 0, 0.46), 0 32px 72px rgba(0, 0, 0, 0.58)",
      heroGradient:
        "radial-gradient(90% 120% at 12% 0%, rgba(10, 132, 255, 0.24) 0%, rgba(10, 132, 255, 0) 58%), radial-gradient(70% 110% at 95% 12%, rgba(125, 122, 255, 0.22) 0%, rgba(125, 122, 255, 0) 62%)",
      surfaceGradient:
        "radial-gradient(90% 120% at 12% 0%, rgba(10, 132, 255, 0.06) 0%, rgba(10, 132, 255, 0) 58%), radial-gradient(70% 110% at 95% 12%, rgba(125, 122, 255, 0.055) 0%, rgba(125, 122, 255, 0) 62%)",
      tableHead: "#1c1c1e",
      tableStripe: "#171719",
      tableHover: "rgba(10, 132, 255, 0.14)",
      tableMobileBackground: "#0a0a0c",
      tableScrollbar: "#48484a",
    },
  },
};

export function getDesignColors(mode = "light") {
  return DESIGN_TOKENS.mode[mode === "dark" ? "dark" : "light"];
}

export default DESIGN_TOKENS;
